import styled from '@emotion/styled'
import { memo } from 'react'
import {
    getEstimatedPinnedRoomWidth,
    getShowChat,
} from '../../../../../app-state/state.screen'
import { isTouchDevice } from '../../../../../utilities/browser'
import { useRooms } from '../../../../Hooks/useRooms'
import layers from '../../../../styles/layers'
import shape from '../../../../styles/shape'
import { PINNED_ROOM_CHAT_MIN_WIDTH } from '../../../../theme'
import PinnedRoom from './PinnedRoom'

const commonStyles = {
    maxWidth: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow: layers.two,
} as const

const PhonePortraitPinnedRoomContainer = styled('div')({
    maxWidth: '100%',
    boxShadow: layers.two,
    overflow: 'hidden',
})
const PhoneLandscapePinnedRoomContainer = styled('div')({
    maxHeight: '100%',
    width: '100%',
    ...commonStyles,
})

const PortraitPinnedRoomsContainer = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    maxWidth: '100%',
    boxShadow: layers.two,
    overflow: 'hidden',
    borderRadius: shape.borderRadiusSmallMedium,
})
const LandscapePinnedRoomsContainer = styled('div')({
    height: '100%',
    // overflowX: 'hidden',
    overflowY: 'auto',
    // ...NO_SCROLLBAR_STYLES,
})

interface PinnedRoomsProps {
    isMobile: boolean
    darkMode: boolean
    pinnedRooms: Array<number>
    roomWidth: number
    sidePins?: boolean
    singlePin?: boolean
}

const PinnedRooms = memo((props: PinnedRoomsProps) => {
    const { isMobile, darkMode, pinnedRooms, roomWidth, sidePins, singlePin } =
        props
    const rooms = useRooms()
    const Room = (pinnedId: number, index: number) => {
        const room = rooms[pinnedId]
        const calculatedShowChat = getShowChat({
            isVertical: !sidePins,
            isMobileDevice: isMobile,
            isTabletDevice: !isMobile && isTouchDevice,
            pinContainerWidth: roomWidth,
            pinnedRooms,
        })
        // potentially override showChat if we're in vertical
        const showChat = sidePins ? room.showChat : calculatedShowChat

        const videoWidth = getEstimatedPinnedRoomWidth({
            width: roomWidth,
            vertical: !sidePins,
            singlePin,
            showChat,
            pinCount: pinnedRooms.length,
        })
        const width = showChat
            ? videoWidth + PINNED_ROOM_CHAT_MIN_WIDTH
            : videoWidth
        // console.log('[Room]', pinnedId, {
        //     showChat,
        //     width,
        //     sidePins,
        //     videoWidth,
        //     roomWidth,
        //     calculatedShowChat,
        // })
        return (
            <PinnedRoom
                key={`pinned-${pinnedId}`}
                roomId={pinnedId}
                // height={roomHeight}
                width={width}
                isMobile={isMobile}
                // showChat={showChat}
                singlePin={singlePin}
                darkMode={darkMode}
                vertical={!sidePins}
                showChat={showChat}
                index={index}
            />
        )
    }
    const [pinnedId] = Array.from(pinnedRooms)

    if (pinnedRooms.length === 0) return null
    if (isMobile) {
        // mobile devices
        if (singlePin) {
            // phone
            if (!sidePins) {
                // phone portrait
                return (
                    <PhonePortraitPinnedRoomContainer>
                        {Room(pinnedId, 0)}
                    </PhonePortraitPinnedRoomContainer>
                )
            } else {
                // phone landscape
                return (
                    <PhoneLandscapePinnedRoomContainer
                        style={{
                            height: '100%',
                            width: roomWidth,
                            minWidth: roomWidth,
                        }}
                    >
                        {Room(pinnedId, 0)}
                    </PhoneLandscapePinnedRoomContainer>
                )
            }
        } else {
            // tablet
            if (!sidePins) {
                // tablet portrait
                const pins: Array<React.ReactNode> = []
                pinnedRooms.forEach((pinnedId, index) => {
                    pins.push(Room(pinnedId, index))
                    // if (pins.length === 1)
                    //     pins.push(<PinDragHelper sidebar={false} />)
                })
                return (
                    <PortraitPinnedRoomsContainer>
                        {pins}
                    </PortraitPinnedRoomsContainer>
                )
            } else {
                // tablet landscape
                const pins: Array<React.ReactNode> = []
                pinnedRooms.forEach((pinnedId, index) => {
                    pins.push(Room(pinnedId, index))
                    // if (pins.length === 1)
                    //     pins.push(<PinDragHelper sidebar={false} />)
                })
                return (
                    <LandscapePinnedRoomsContainer
                        className="no-scrollbars"
                        style={{
                            height: '100%',
                            width: roomWidth,
                            minWidth: roomWidth,
                            maxWidth: roomWidth,
                        }}
                    >
                        {pins}
                    </LandscapePinnedRoomsContainer>
                )
            }
        }
    } else {
        // desktop
        if (!sidePins) {
            // portrait
            const pins: Array<React.ReactNode> = []
            pinnedRooms.forEach((pinnedId, index) => {
                pins.push(Room(pinnedId, index))
                // if (pins.length === 1)
                //     pins.push(<PinDragHelper sidebar={false} />)
            })
            return (
                <PortraitPinnedRoomsContainer>
                    {pins}
                </PortraitPinnedRoomsContainer>
            )
        } else {
            // landscape
            const pins: Array<React.ReactNode> = []
            pinnedRooms.forEach((pinnedId, index) => {
                pins.push(Room(pinnedId, index))
                // if (pins.length === 1)
                //     pins.push(<PinDragHelper sidebar={false} />)
            })
            return (
                <LandscapePinnedRoomsContainer
                    className="no-scrollbars"
                    style={{
                        height: '100%',
                        width: roomWidth,
                        minWidth: roomWidth,
                        maxWidth: roomWidth,
                    }}
                >
                    {pins}
                </LandscapePinnedRoomsContainer>
            )
        }
    }
})

export default PinnedRooms
